/* eslint-disable */
import Axios from 'axios'
import type { AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'

export const SERVICE_ERROR_MESSAGE = '服务异常，请稍后重试'
const ERROR_MESSAGE_MAX_LENGTH = 80
export const ERROR_MESSAGE_REPLACED_KEY = '__geoErrorMessageReplaced'

export interface NormalizedErrorMessage {
  message: string
  replaced: boolean
}

/**
 * 保留简短的业务提示；超长文本、堆栈和 HTML 等技术细节统一替换。
 * 固定以“服务异常”开头，便于仅凭用户截图识别是前端兜底文案。
 */
export function normalizeErrorMessage(message: unknown): NormalizedErrorMessage {
  if (typeof message !== 'string') {
    return { message: SERVICE_ERROR_MESSAGE, replaced: true }
  }

  const compact = message.replace(/\s+/g, ' ').trim()
  const lineCount = message.split(/\r?\n/).length
  const looksLikeTechnicalDump =
    lineCount > 3 ||
    /(?:caused by:|\bexception\b|\n\s*at\s+|<!doctype\s+html|<html[\s>]|"stack"\s*:)/i.test(message)

  if (!compact || compact.length > ERROR_MESSAGE_MAX_LENGTH || looksLikeTechnicalDump) {
    return { message: SERVICE_ERROR_MESSAGE, replaced: true }
  }
  return { message: compact, replaced: false }
}

/** 统一整理请求异常，让全局提示与业务页面的 catch 都只能读到可展示文案。 */
export function normalizeRequestError(error: any): NormalizedErrorMessage {
  if (error?.[ERROR_MESSAGE_REPLACED_KEY] === true) {
    return { message: error.message || SERVICE_ERROR_MESSAGE, replaced: true }
  }
  const source =
    error?.response?.data?.message ?? error?.data?.message ?? error?.message ?? error
  const normalized = normalizeErrorMessage(source)

  if (error && typeof error === 'object') {
    error.message = normalized.message
    error[ERROR_MESSAGE_REPLACED_KEY] = normalized.replaced
    if (error.data && typeof error.data === 'object') {
      error.data.message = normalized.message
      error.data[ERROR_MESSAGE_REPLACED_KEY] = normalized.replaced
    }
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.message = normalized.message
      error.response.data[ERROR_MESSAGE_REPLACED_KEY] = normalized.replaced
    }
  }
  return normalized
}

export function responseHandle(response: AxiosResponse) {
  const res = response.data
  const list = ['x-pkcs7-certificates', 'vnd.ms-excel', 'pem', 'zip', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'octet-stream']
  const flag = list.some(x => String(response.headers['content-type'] || '').indexOf(x) > -1)
  if (response.headers['content-type'] != null && flag) {
    return response
  }

  if (res?.code === 200 || res?.code === 333) return res
  switch (res?.code) {
    case 401:
      // 未登录
      if (process.env.VUE_APP_CONSOLE_LOGIN)
        window.location.href = `${process.env.VUE_APP_CONSOLE_LOGIN
          }?backUrl=${encodeURI(window.location.href)}`
      break
    case 410:
      // 无权限
      window.location.href = `${window.location.origin}/404`
      break
    default:
      break
  }
  const error = res || response
  normalizeRequestError(error)
  return Promise.reject(error)
}

export function consoleError(error: any) {
  console.log('error：', error)
  console.log('error.message', error.message)
  console.log('error.response', error.response)
}

export function httpErrorStatusHandle(error: any) {
  const normalized = normalizeRequestError(error)
  let message = normalized.message
  if (Axios.isCancel(error)) {
    return console.error(`重复请求：${message}`)
  }
  if (error?.response) {
    switch (error.response?.status) {
      case 302:
        message = '接口重定向了！'
        break
      case 400:
        message = '参数不正确！'
        break
      case 401:
        message = '您未登录，或者登录已经超时，请先登录！'
        break
      case 403:
        message = '您没有权限操作！'
        break
      case 404:
        message = `请求地址出错：${error?.config?.url}`
        break
      case 408:
        message = '请求超时！'
        break
      case 409:
        message = '系统已存在相同数据！'
        break
      case 500:
        message = '服务器内部错误！'
        break
      case 501:
        message = '服务未实现！'
        break
      case 502:
        message = '网关错误！'
        break
      case 503:
        message = '服务不可用！'
        break
      case 504:
        message = '服务暂时无法访问，请稍后再试！'
        break
      case 505:
        message = 'HTTP 版本不受支持！'
        break
      default:
        message = '异常问题，请联系管理员！'
        break
    }
  }
  if (error?.message?.includes('timeout')) message = '网络请求超时！'
  if (error?.message?.includes('Network')) {
    message = window.navigator.onLine ? '服务端异常！' : '您断网了！'
  }

  if (message) {
    ElMessage({
      type: 'error',
      message,
    })
  }
  return Promise.reject(error)
}
